/**
 * gab-ai APM client SDK — browser-side (web) error + performance capture.
 *
 * Web-only for v1 (gated by Platform.OS === 'web' in _layout.jsx). The SDK
 * uses browser standards: window.onerror, PerformanceObserver, fetch,
 * navigator.sendBeacon, localStorage. Native targets (iOS/Android/macOS)
 * are not instrumented yet.
 *
 * Auth: ingestion requires a logged-in user. Pass `getAuthToken` returning
 * the current JWT; the SDK adds `Authorization: Bearer <token>` to every
 * flush. If no token is available the buffer is held (not flushed) up to
 * 2× maxBuffer, then oldest events drop — so pre-login noise never reaches
 * the server.
 *
 * Ported from mwcs/packages/apm/src/client.ts. Adapted for gab-ai:
 *   - Bearer-token auth (mwcs used an anon session header)
 *   - Errors bypass sampling (always captured)
 *   - Fetch auto-instrument enabled by default (skips the APM endpoint to
 *     avoid a feedback loop)
 *   - Endpoint defaults to /json/apm/events (the auth-required prefix)
 */

import { parseStack, cleanMessage } from './apm-format';

// ---------------------------------------------------------------------------
// Config + state
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} ApmClientConfig
 * @property {string} endpoint                       POST URL for event ingestion. Default: '/json/apm/events'.
 * @property {() => (string | null | undefined)} [getAuthToken]  Returns the current JWT token. If null, flush is held.
 * @property {string} [release]                      App release / version.
 * @property {number} [sampleRate]                   Sample rate [0..1] for non-error events. Default: 1.
 * @property {number} [maxBuffer]                    Max events to buffer before flushing. Default: 20.
 * @property {number} [flushIntervalMs]              Max ms between flushes. Default: 5000.
 * @property {{errors?: boolean, vitals?: boolean, console?: boolean, fetch?: boolean}} [autoCapture]
 * @property {string} [userId]                       Override anon user id (auto-generated from localStorage otherwise).
 */

class ApmClient {
  constructor() {
    /** @type {Required<Omit<ApmClientConfig, 'getAuthToken'>>} & { getAuthToken?: () => (string | null | undefined)} */
    this.config = {
      endpoint: '/json/apm/events',
      getAuthToken: undefined,
      release: 'dev',
      sampleRate: 1,
      maxBuffer: 20,
      flushIntervalMs: 5000,
      autoCapture: { errors: true, vitals: true, console: false, fetch: true },
      userId: '',
    };
    /** @type {any[]} */
    this.buffer = [];
    /** @type {ReturnType<typeof setInterval> | null} */
    this.flushTimer = null;
    this.sessionId = '';
    this.started = false;
    this.pendingSpans = new Map();
    // Original references for uninstall on destroy().
    this.origFetch = null;
    this.origXhrOpen = null;
    this.origXhrSend = null;
    this.origConsoleError = null;
    this.prevOnError = null;
    this.prevOnRejection = null;
    // Cap the buffer when no auth token is available (avoids unbounded growth
    // for pre-login users). 2× maxBuffer gives a grace window after login.
    this.maxHoldBuffer = 0;
  }

  /** Boot the SDK. Safe to call once. */
  init(partial) {
    if (this.started) return;
    this.started = true;
    this.config = {
      ...this.config,
      ...partial,
      autoCapture: { ...this.config.autoCapture, ...(partial && partial.autoCapture ? partial.autoCapture : {}) },
    };
    this.maxHoldBuffer = this.config.maxBuffer * 2;
    // Device/browser context — captured once, attached to every event as tags
    // so error details show the user's environment (browser, OS, screen, etc.)
    this.deviceContext = {};
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
      var ua = navigator.userAgent || '';
      var platform = navigator.platform || '';
      this.deviceContext = {
        browser: this._parseBrowser(ua),
        os: this._parseOS(ua),
        platform: platform,
        screen: (window.screen ? window.screen.width + 'x' + window.screen.height : ''),
        viewport: (window.innerWidth ? window.innerWidth + 'x' + window.innerHeight : ''),
        lang: navigator.language || '',
        ua: ua,
      };
    }

    if (typeof window === 'undefined') return;

    this.sessionId = this.config.userId || this.getOrCreateUserId();
    this.flushTimer = setInterval(() => this.flush(), this.config.flushIntervalMs);
    // Flush on unload.
    window.addEventListener('pagehide', () => this.flush({ useBeacon: true }));
    window.addEventListener('beforeunload', () => this.flush({ useBeacon: true }));

    const ac = this.config.autoCapture;
    if (ac.errors) this.installErrorCapture();
    if (ac.vitals) this.installVitalsCapture();
    if (ac.console) this.installConsoleCapture();
    if (ac.fetch) this.installFetchCapture();
  }

  /** Disable all hooks + flush remaining buffer. */
  destroy() {
    if (!this.started) return;
    this.started = false;
    if (this.flushTimer) clearInterval(this.flushTimer);
    this.flush({ useBeacon: true });
    if (typeof window === 'undefined') return;
    if (this.prevOnError !== null) window.onerror = this.prevOnError;
    if (this.prevOnRejection !== null) window.removeEventListener('unhandledrejection', this.onRejection);
    if (this.origConsoleError) console.error = this.origConsoleError;
    if (this.origFetch) window.fetch = this.origFetch;
    if (this.origXhrOpen) XMLHttpRequest.prototype.open = this.origXhrOpen;
    if (this.origXhrSend) XMLHttpRequest.prototype.send = this.origXhrSend;
  }

  // -----------------------------------------------------------------------
  // Manual capture API
  // -----------------------------------------------------------------------

  captureError(err, tags) {
    const message = err instanceof Error ? cleanMessage(err.message) : cleanMessage(String(err));
    const stack = err instanceof Error ? err.stack : undefined;
    this.push({
      id: '',
      timestamp: Date.now(),
      type: 'error',
      level: 'error',
      environment: 'client',
      message,
      stack,
      frames: stack ? parseStack(stack) : undefined,
      release: this.config.release,
      tags,
      user: { id: this.sessionId, session: this.sessionId },
    });
  }

  captureMessage(message, level, tags) {
    this.push({
      id: '',
      timestamp: Date.now(),
      type: 'log',
      level: level || 'info',
      environment: 'client',
      message,
      release: this.config.release,
      tags,
      user: { id: this.sessionId, session: this.sessionId },
    });
  }

  timing(metric, value, unit, tags) {
    this.push({
      id: '',
      timestamp: Date.now(),
      type: 'timing',
      level: 'info',
      environment: 'client',
      metric,
      value,
      unit: unit || 'ms',
      release: this.config.release,
      tags,
      user: { id: this.sessionId, session: this.sessionId },
    });
  }

  startSpan(name, tags) {
    const id = Math.random().toString(36).slice(2);
    const startedAt = Date.now();
    this.pendingSpans.set(id, { name, startedAt, tags });
    return {
      end: (extraTags) => {
        const p = this.pendingSpans.get(id);
        if (!p) return;
        this.pendingSpans.delete(id);
        const durationMs = Date.now() - p.startedAt;
        this.push({
          id: '',
          timestamp: p.startedAt,
          type: 'span',
          level: 'info',
          environment: 'client',
          name: p.name,
          durationMs,
          release: this.config.release,
          tags: { ...p.tags, ...extraTags },
          user: { id: this.sessionId, session: this.sessionId },
        });
      },
    };
  }

  // -----------------------------------------------------------------------
  // Internal: queue + transport
  // -----------------------------------------------------------------------

  push(event) {
    if (!this.started) return;
    // Merge device context into every event's tags.
    if (this.deviceContext && Object.keys(this.deviceContext).length) {
      event.tags = Object.assign({}, this.deviceContext, event.tags || {});
    }
    // Errors + fatals ALWAYS bypass sampling.
    const isCritical = event.level === 'error' || event.level === 'fatal';
    if (!isCritical && this.config.sampleRate < 1 && Math.random() > this.config.sampleRate) return;
    this.buffer.push(event);
    // If buffer grows beyond the hold cap (e.g. pre-login user with no token),
    // drop the oldest to bound memory.
    if (this.buffer.length > this.maxHoldBuffer) {
      this.buffer.splice(0, this.buffer.length - this.maxHoldBuffer);
    }
    if (this.buffer.length >= this.config.maxBuffer) this.flush();
  }

  flush(opts) {
    opts = opts || {};
    if (this.buffer.length === 0) return;

    // Auth gate: don't flush without a token (server requires login). Hold
    // the buffer for next time — except on unload where we try anyway (the
    // server will 401 and drop it).
    const token = this.config.getAuthToken ? this.config.getAuthToken() : null;
    if (!token && !opts.useBeacon) return;

    const events = this.buffer;
    this.buffer = [];
    const payload = JSON.stringify({ events });
    try {
      // Prefer sendBeacon on unload — fire-and-forget, survives navigation.
      if (opts.useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const blob = new Blob([payload], { type: 'text/plain' });
        if (navigator.sendBeacon(this.config.endpoint, blob)) return;
      }
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      fetch(this.config.endpoint, {
        method: 'POST',
        headers,
        body: payload,
        credentials: 'include',
        keepalive: true,
      }).catch(() => { /* silent */ });
    } catch {
      /* silent */
    }
  }

  // -----------------------------------------------------------------------
  // Auto-capture installers
  // -----------------------------------------------------------------------

  installErrorCapture() {
    if (typeof window === 'undefined') return;
    this.prevOnError = window.onerror;
    window.onerror = (msg, src, lineno, colno, err) => {
      // Suppress known-benign browser noise that we can't act on.
      if (typeof msg === 'string' && (
        msg === 'ResizeObserver loop completed with undelivered notifications.' ||
        msg === 'ResizeObserver loop limit exceeded'
      )) {
        return false;
      }
      this.captureError(err instanceof Error ? err : new Error(String(msg)), {
        source: src ? String(src) : '',
        line: lineno != null ? String(lineno) : '',
        col: colno != null ? String(colno) : '',
      });
      if (typeof this.prevOnError === 'function') {
        return this.prevOnError(msg, src, lineno, colno, err);
      }
      return false;
    };
    window.addEventListener('unhandledrejection', this.onRejection);
  }

  onRejection = (ev) => {
    const reason = ev.reason;
    if (reason instanceof Error) this.captureError(reason, { source: 'promise' });
    else this.captureMessage(`Unhandled rejection: ${String(reason)}`, 'error', { source: 'promise' });
  };

  installVitalsCapture() {
    if (typeof window === 'undefined') return;
    const record = (metric, value, rating) => {
      this.push({
        id: '',
        timestamp: Date.now(),
        type: 'vital',
        level: rating === 'good' ? 'info' : rating === 'needs-improvement' ? 'warning' : 'error',
        environment: 'client',
        metric,
        value,
        rating,
        release: this.config.release,
        user: { id: this.sessionId, session: this.sessionId },
      });
    };

    const rating = (val, good, poor) =>
      val <= good ? 'good' : val >= poor ? 'poor' : 'needs-improvement';

    try {
      const po = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'largest-contentful-paint') {
            const value = entry.startTime;
            record('LCP', value, rating(value, 2500, 4000));
          }
        }
      });
      po.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch { /* unsupported */ }

    try {
      const po2 = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'first-input' && entry.processingStart !== undefined) {
            const value = entry.processingStart - entry.startTime;
            record('FID', value, rating(value, 100, 300));
          }
        }
      });
      po2.observe({ type: 'first-input', buffered: true });
    } catch { /* unsupported */ }

    try {
      let cls = 0;
      const po3 = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'layout-shift' && !entry.hadRecentInput && typeof entry.value === 'number') {
            cls += entry.value;
          }
        }
        record('CLS', cls * 1000, rating(cls * 1000, 100, 250));
      });
      po3.observe({ type: 'layout-shift', buffered: true });
    } catch { /* unsupported */ }
  }

  installConsoleCapture() {
    if (typeof console === 'undefined') return;
    this.origConsoleError = console.error;
    // eslint-disable-next-line no-console
    console.error = (...args) => {
      const first = args[0];
      if (first instanceof Error) this.captureError(first, { source: 'console' });
      else this.captureMessage(args.map((a) => String(a)).join(' '), 'error', { source: 'console' });
      this.origConsoleError.apply(console, args);
    };
  }

  installFetchCapture() {
    if (typeof window === 'undefined' || !window.fetch) return;
    this.origFetch = window.fetch;
    const orig = this.origFetch;
    const endpoint = this.config.endpoint;
    const pushTiming = (metric, value, tags) => this.timing(metric, value, 'ms', tags);

    window.fetch = async function (input, init) {
      const url = typeof input === 'string' ? input : input instanceof Request ? input.url : String(input);
      const method = (init && init.method) || 'GET';
      const start = Date.now();
      try {
        const res = await orig.call(window, input, init);
        const status = res.status;
        // Skip the APM endpoint itself — recording it would create a feedback
        // loop (the timing event triggers another POST to the same URL).
        if (!url || url.indexOf(endpoint) === -1) {
          pushTiming(`fetch ${method} ${status}`, Date.now() - start, { url, method, status: String(status) });
        }
        return res;
      } catch (err) {
        if (!url || url.indexOf(endpoint) === -1) {
          pushTiming('fetch error', Date.now() - start, { url, method, status: 'error' });
        }
        throw err;
      }
    };

    // XHR instrumentation.
    this.origXhrOpen = XMLHttpRequest.prototype.open;
    this.origXhrSend = XMLHttpRequest.prototype.send;
    const origOpen = this.origXhrOpen;
    const origSend = this.origXhrSend;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__apmMethod = String(method);
      this.__apmUrl = String(url);
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      const xhr = this;
      const start = Date.now();
      xhr.addEventListener('loadend', () => {
        const u = xhr.__apmUrl || '';
        if (u && u.indexOf(endpoint) !== -1) return; // skip APM endpoint
        pushTiming(
          `xhr ${xhr.__apmMethod || 'GET'} ${xhr.status || 'error'}`,
          Date.now() - start,
          { method: xhr.__apmMethod || 'GET', url: u, status: String(xhr.status || 'error') },
        );
      });
      return origSend.apply(this, arguments);
    };
  }

  getOrCreateUserId() {
    try {
      const KEY = 'gab:apm:uid';
      let id = localStorage.getItem(KEY);
      if (!id) {
        id = 'u_' + Math.random().toString(36).slice(2, 10);
        localStorage.setItem(KEY, id);
      }
      return id;
    } catch {
      return 'u_anon';
    }
  }

  _parseBrowser(ua) {
    if (/Edg\//.test(ua)) return 'Edge';
    if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) return 'Chrome';
    if (/Firefox\//.test(ua)) return 'Firefox';
    if (/Safari\//.test(ua) && !/Chrome/.test(ua)) return 'Safari';
    if (/OPR\//.test(ua)) return 'Opera';
    return 'other';
  }

  _parseOS(ua) {
    if (/Windows NT 10/.test(ua)) return 'Windows';
    if (/Mac OS X/.test(ua)) return 'macOS';
    if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
    if (/Android/.test(ua)) return 'Android';
    if (/Linux/.test(ua)) return 'Linux';
    return 'other';
  }
}

/** Singleton. */
export const apm = new ApmClient();

/** Boot the singleton client. */
export function initApm(config) {
  apm.init(config);
}

export { ApmClient };
